import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { addHandlerToClub } from "./api";
import { getNextTierWarning } from "./pricing";
import type { Club, Handler } from "./types";
import DialogHeader from "./DialogHeader";
import { MemberContent } from "./MemberListItem";
import { ListItemButton } from "@mui/material";

interface AddClubMemberDialogProps {
  open: boolean;
  club: Club | null;
  handlers: Handler[];
  handlersLoading: boolean;
  onEnsureHandlersLoaded: () => void;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const MIN_SEARCH_LENGTH = 5;

const AddClubMemberDialog: React.FC<AddClubMemberDialogProps> = ({
  open,
  club,
  handlers,
  handlersLoading,
  onEnsureHandlersLoaded,
  onClose,
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      onEnsureHandlersLoaded();
    }
  }, [open, onEnsureHandlersLoaded]);

  const memberCount = club?.members?.length ?? 0;
  const warning = club ? getNextTierWarning(memberCount) : null;

  const query = searchQuery.toLowerCase();
  const filteredHandlers = handlers.filter(
    (h) =>
      h.handler_name.toLowerCase().includes(query) ||
      h.email_address.toLowerCase().includes(query),
  );
  const availableHandlers = club
    ? filteredHandlers.filter(
        (h) => !club.members.some((m) => m.handler_id === h.handler_id),
      )
    : filteredHandlers;

  const showResults = searchQuery.trim().length >= MIN_SEARCH_LENGTH;

  const handleAdd = async (handlerId: number) => {
    if (!club) return;
    try {
      setAdding(true);
      await addHandlerToClub(club.id, handlerId);
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error adding member to club:", err);
      onError("Failed to add member. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
    >
      <DialogHeader title="Add Member to Club" onClose={onClose} />
      <DialogContent dividers>
        {warning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This club is at the limit of its current plan. Adding this member
            will move you to the <strong>{warning.nextLabel} plan</strong> (
            {warning.nextPrice}).
          </Alert>
        )}
        <TextField
          fullWidth
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          helperText={
            !showResults
              ? `Enter at least ${MIN_SEARCH_LENGTH} characters to search`
              : undefined
          }
          InputProps={{
            startAdornment: (
              <Search sx={{ mr: 1, color: "text.secondary" }} />
            ),
          }}
          sx={{ mb: 2 }}
        />

        {!showResults ? null : handlersLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : availableHandlers.length === 0 ? (
          <Alert severity="info">No handlers found matching your search.</Alert>
        ) : (
          <List sx={{ maxHeight: { xs: "none", sm: 400 }, overflow: "auto" }}>
            {availableHandlers.map((handler) => (
              <ListItemButton
                key={handler.handler_id}
                onClick={() => handleAdd(handler.handler_id)}
                disabled={adding}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  border: 1,
                  borderColor: "divider",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.light + "10",
                  },
                }}
              >
                <MemberContent
                  member={handler}
                  avatarColor={theme.palette.primary.main}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={adding}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddClubMemberDialog;
